export type EnforcementType = 'statutory' | 'private';

type EnforcementContext = {
  issuing_authority?: string | null;
  document_type?: string | null;
  document_intent?: string | null;
  key_phrases?: string[] | null;
  stage?: string | null;
};

const STATUTORY_AUTHORITY_HINTS = [
  'council',
  'borough',
  'transport for london',
  'tfl',
  'city of',
  'county council',
  'district council',
  'metropolitan',
  'london borough',
  'local authority'
];

const PRIVATE_AUTHORITY_HINTS = [
  'parkingeye',
  'uk parking control',
  'ukpc',
  'euro car parks',
  'apcoa',
  'smart parking',
  'horizon parking',
  'excel parking',
  'civil enforcement',
  'parking control',
  'cp plus',
  'parking charge',
  'parking management',
  'parking enforcement'
];

const STATUTORY_DOC_HINTS = [
  'penalty charge notice',
  'notice to owner',
  'charge certificate',
  'order for recovery',
  'traffic management act',
  'tma 2004',
  'traffic enforcement centre',
  'tec',
  'te7',
  'te9',
  'pe2',
  'pe3',
  'statutory declaration'
];

const PRIVATE_DOC_HINTS = [
  'parking charge notice',
  'notice to keeper',
  'notice to driver',
  'protection of freedoms act',
  'pofa',
  'popla',
  'ias',
  'independent appeals service',
  'british parking association',
  'bpa',
  'international parking community',
  'ipc',
  'debt recovery',
  'letter before claim',
  'letter before action',
  'court claim',
  'county court'
];

const includesAny = (text: string, hints: string[]) => hints.some((hint) => text.includes(hint));

export const inferEnforcementType = (context: EnforcementContext): EnforcementType => {
  const authority = String(context.issuing_authority || '').toLowerCase();
  const stage = String(context.stage || '').toLowerCase();
  const text = [
    authority,
    stage,
    context.document_intent || '',
    ...(context.key_phrases || [])
  ]
    .join(' ')
    .toLowerCase();

  const authorityLooksStatutory = includesAny(authority, STATUTORY_AUTHORITY_HINTS);
  const authorityLooksPrivate = includesAny(authority, PRIVATE_AUTHORITY_HINTS) ||
    (authority.includes('parking') && (authority.includes('ltd') || authority.includes('limited') || authority.includes('plc')));

  if (authorityLooksStatutory && !authorityLooksPrivate) return 'statutory';
  if (authorityLooksPrivate && !authorityLooksStatutory) return 'private';

  const documentType = String(context.document_type || '').toUpperCase();
  if (['ORDER_FOR_RECOVERY', 'CHARGE_CERTIFICATE', 'NTO'].includes(documentType)) return 'statutory';
  if (includesAny(text, PRIVATE_DOC_HINTS)) return 'private';
  if (includesAny(text, STATUTORY_DOC_HINTS)) return 'statutory';

  return authorityLooksPrivate ? 'private' : 'statutory';
};
